import { useId, useMemo } from "react";
import type { ReactElement } from "react";
import type { LayerVisibility } from "../../state/layerState";
import type { SolarPotentialReport } from "../../score/computeSolarPotential";
import {
  InspectorChipButton,
  InspectorChipButtonGrid,
  InspectorChipButtonRow
} from "./InspectorChipButton";
import { InspectorRow } from "./InspectorRow";
import { Spacer } from "../Spacer";
import { SidetextRow } from "./SidetextRow";
import { SubsectionHeader } from "./SubsectionHeader";

export const SUN_VISIBILITY_KEYS = [
  "sunPath",
  "sunShadows",
  "solarGain"
] as const;

export type SunVisibilityKey = (typeof SUN_VISIBILITY_KEYS)[number];

const VIS_LABELS: Record<SunVisibilityKey, string> = {
  sunPath: "Sun path arc",
  sunShadows: "Cast shadows",
  solarGain: "Solar gain heatmap"
};

export type SunPresetId =
  | "winter"
  | "equinox"
  | "summer"
  | "now"
  | "solarNoon";

export interface SunLayersCardProps {
  visibility: LayerVisibility;
  dateTime: Date;
  onToggle: (key: SunVisibilityKey, value: boolean) => void;
  onMonthDayHourChange: (
    month1: number,
    day: number,
    hourFloat: number,
    phase: "drag" | "commit"
  ) => void;
  onPreset: (preset: SunPresetId) => void;
  /**
   * Terrain-adjusted PVWatts annual production. Null until `/v1/solar`
   * resolves or when the worker has no NREL key — the row renders `—`
   * in that case so the layout stays stable.
   */
  solar: SolarPotentialReport | null;
  presentation?: "inspector" | "drawer";
  /** Visibility keys whose value differs from the viewer default (accent dot). */
  modifiedKeys?: ReadonlySet<string>;
}

function solarValue(report: SolarPotentialReport | null): string {
  if (!report) return "—";
  const kwh = report.annualKwh.toLocaleString();
  const suffix = report.terrainAdjusted ? " (terrain-adjusted)" : "";
  return `~${kwh} kWh/yr${suffix}`;
}

function monthAbbrev(month1: number): string {
  const d = new Date(2000, month1 - 1, 1);
  return d.toLocaleString(undefined, { month: "short" });
}

function formatHourLabel(hourFloat: number): string {
  const h = Math.floor(hourFloat);
  const m = Math.round((hourFloat - h) * 60);
  const total = h + m / 60;
  return total.toFixed(1);
}

/** Last quarter-hour tick — 24.0 would roll the calendar via Date#setHours. */
const MAX_SUN_HOUR_FLOAT = 23.75;

/** Slider uses 15-minute steps (0.25 h) matching the inspector sun rails. */
function clampQuarterHour(hourFloat: number): number {
  const q = Math.round(hourFloat * 4) / 4;
  return Math.min(MAX_SUN_HOUR_FLOAT, Math.max(0, q));
}

export function SunLayersCard({
  visibility,
  dateTime,
  onToggle,
  onMonthDayHourChange,
  onPreset,
  solar,
  presentation = "inspector",
  modifiedKeys
}: SunLayersCardProps): ReactElement {
  const reactId = useId();

  const { month1, day, hourPrecise, sliderHour, hourDisplay } = useMemo(() => {
    const dt =
      dateTime instanceof Date && !Number.isNaN(dateTime.getTime())
        ? dateTime
        : new Date(0);
    const m1 = dt.getMonth() + 1;
    const d = dt.getDate();
    const hp = dt.getHours() + dt.getMinutes() / 60;
    const sh = clampQuarterHour(hp);
    return {
      month1: m1,
      day: d,
      hourPrecise: hp,
      sliderHour: sh,
      hourDisplay: formatHourLabel(hp)
    };
  }, [dateTime]);

  const daysInTargetMonth = useMemo(() => {
    const dt =
      dateTime instanceof Date && !Number.isNaN(dateTime.getTime())
        ? dateTime
        : new Date(0);
    const y = dt.getFullYear();
    const m0 = month1 - 1;
    return new Date(y, m0 + 1, 0).getDate();
  }, [dateTime, month1]);

  return (
      <div>
        <SubsectionHeader label="Display" first />
        {SUN_VISIBILITY_KEYS.map((key) => (
          <InspectorRow
            key={key}
            label={VIS_LABELS[key]}
            labelWeight="normal"
            checkboxId={`${reactId}-vis-${key}`}
            checked={visibility[key]}
            modified={modifiedKeys?.has(key) === true}
            onCheckedChange={(v) => {
              onToggle(key, v);
            }}
          />
        ))}
        <SubsectionHeader label="Date & time" />
        <InspectorRow
          label="Month"
          labelWeight="normal"
          fieldRow
          inspectorSliderTrailing={{
            sliderLabelWide: false,
            sliderId: `${reactId}-sun-month-r`,
            boxInputId: `${reactId}-sun-month-i`,
            ariaLabel: "Month of year",
            min: 1,
            max: 12,
            step: 1,
            value: month1,
            boxDisplayValue: monthAbbrev(month1),
            onSliderInput: (next) => {
              const dim = new Date(
                dateTime.getFullYear(),
                next,
                0
              ).getDate();
              const dClamped = Math.min(day, dim);
              onMonthDayHourChange(next, dClamped, hourPrecise, "drag");
            },
            onSliderCommit: () => {
              onMonthDayHourChange(month1, day, hourPrecise, "commit");
            },
            parseBoxValue: (raw) => {
              const n = Number.parseInt(raw, 10);
              if (Number.isFinite(n) && n >= 1 && n <= 12) {
                return n;
              }
              return undefined;
            },
            formatBoxDraft: (v) => String(Math.round(v)),
            onBoxParsed: (canonical) => {
              const dim = new Date(
                dateTime.getFullYear(),
                canonical,
                0
              ).getDate();
              const dClamped = Math.min(day, dim);
              onMonthDayHourChange(
                canonical,
                dClamped,
                hourPrecise,
                "commit"
              );
            }
          }}
        />
        <InspectorRow
          label="Day"
          labelWeight="normal"
          fieldRow
          inspectorSliderTrailing={{
            sliderLabelWide: false,
            sliderId: `${reactId}-sun-day-r`,
            boxInputId: `${reactId}-sun-day-i`,
            ariaLabel: "Day of month",
            min: 1,
            max: Math.max(1, daysInTargetMonth),
            step: 1,
            value: Math.min(day, daysInTargetMonth),
            boxDisplayValue: String(Math.min(day, daysInTargetMonth)),
            onSliderInput: (next) => {
              onMonthDayHourChange(month1, next, hourPrecise, "drag");
            },
            onSliderCommit: () => {
              onMonthDayHourChange(
                month1,
                Math.min(day, daysInTargetMonth),
                hourPrecise,
                "commit"
              );
            },
            parseBoxValue: (raw) => {
              const n = Number.parseInt(raw, 10);
              if (Number.isFinite(n) && n >= 1 && n <= 31) {
                return n;
              }
              return undefined;
            },
            formatBoxDraft: (v) => String(Math.round(v)),
            onBoxParsed: (canonical) => {
              const dim = new Date(
                dateTime.getFullYear(),
                month1,
                0
              ).getDate();
              const dClamped = Math.min(Math.max(1, canonical), dim);
              onMonthDayHourChange(month1, dClamped, hourPrecise, "commit");
            }
          }}
        />
        <InspectorRow
          label="Hour"
          labelWeight="normal"
          fieldRow
          inspectorSliderTrailing={{
            sliderLabelWide: false,
            sliderId: `${reactId}-sun-hour-r`,
            boxInputId: `${reactId}-sun-hour-i`,
            ariaLabel: "Time of day, hours",
            min: 0,
            max: MAX_SUN_HOUR_FLOAT,
            step: 0.25,
            value: sliderHour,
            boxDisplayValue: hourDisplay,
            onSliderInput: (next) => {
              onMonthDayHourChange(month1, day, next, "drag");
            },
            onSliderCommit: () => {
              onMonthDayHourChange(month1, day, sliderHour, "commit");
            },
            parseBoxValue: (raw) => {
              const n = Number.parseFloat(raw);
              if (!Number.isFinite(n)) return undefined;
              if (n < 0 || n > MAX_SUN_HOUR_FLOAT) return undefined;
              return clampQuarterHour(n);
            },
            formatBoxDraft: (v) => formatHourLabel(v),
            onBoxParsed: (canonical) => {
              const h = clampQuarterHour(canonical);
              onMonthDayHourChange(month1, day, h, "commit");
            }
          }}
        />
        <Spacer size="xs" noBorder />
        <SidetextRow
          label="Solar potential"
          sideText={solarValue(solar)}
          drawerRail={presentation === "drawer"}
        />
        <SubsectionHeader label="Presets" />
        <InspectorChipButtonGrid>
          <InspectorChipButtonRow>
            <InspectorChipButton
              onClick={() => {
                onPreset("now");
              }}
            >
              Now
            </InspectorChipButton>
            <InspectorChipButton
              onClick={() => {
                onPreset("solarNoon");
              }}
            >
              Solar noon
            </InspectorChipButton>
          </InspectorChipButtonRow>
          <InspectorChipButtonRow>
            <InspectorChipButton
              onClick={() => {
                onPreset("winter");
              }}
            >
              Winter solstice
            </InspectorChipButton>
            <InspectorChipButton
              onClick={() => {
                onPreset("equinox");
              }}
            >
              Spring equinox
            </InspectorChipButton>
          </InspectorChipButtonRow>
          <InspectorChipButton
            fullWidth
            onClick={() => {
              onPreset("summer");
            }}
          >
            Summer solstice
          </InspectorChipButton>
        </InspectorChipButtonGrid>
      </div>
  );
}
